'use client'

import { useEffect, useState } from 'react'
import { buildClientErrorPayload } from '@/lib/clientErrorReport'
import { claimAutoReload, isDomMutationError } from '@/lib/domErrorRecovery'

// Used by error boundaries: reports the error to /api/client_error and, for
// DOM-mutation errors (browser page translation), reloads the page once.
// Returns true while the page is about to reload, so the caller can render
// nothing instead of flashing an error screen.
export function useClientErrorReporter(
    error: Error & { digest?: string },
    prefix = ''
): boolean {
    const [recovering, setRecovering] = useState(false)

    useEffect(() => {
        const autoReload = isDomMutationError(error) && claimAutoReload()
        if (autoReload) setRecovering(true)

        const report = fetch('/api/client_error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(buildClientErrorPayload(error, prefix, { autoReload })),
            keepalive: true,
        }).catch((e) => {
            console.warn('Failed to send error info:', e)
        })

        if (autoReload) {
            // give the report a moment to go out, but never block the reload
            Promise.race([report, new Promise((r) => setTimeout(r, 1500))]).finally(() => {
                window.location.reload()
            })
        }
    }, [error, prefix])

    return recovering
}
